import React, { useState, useEffect } from "react";
import { Form, Card, Button, Row, Col, Modal } from "react-bootstrap";
import { TimePicker } from "antd";
import moment from "moment";
import { useGlobalState } from "../../../services/hostelConst";
import {
  addHostel,
  updateHostel,
} from "../../../services/OrganisationServices/hostelManagmentService";
import {
  callToast,
  showErrorMessages,
} from "../../../services/utilityFunctions";
import "./hostel.css";

const AddHostelComponent = ({ navigate, refresh }) => {
  const { closeModal, modalState } = useGlobalState();
  const masterData = modalState.data;
  console.log("Hostel modal state data:", masterData);

  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    openingTime: null,
    closingTime: null,
    hostelId: null,
  });
  const [errors, setErrors] = useState({});
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "success",
  });

  useEffect(() => {
    if (masterData) {
      console.log("Loading hostel data for editing:", masterData);
      setFormData({
        name: masterData.NAME || masterData.name || "",
        description: masterData.DESCRIPTION || masterData.description || "",
        openingTime:
          masterData["OPENING TIME"] || masterData.openingTime || null,
        closingTime:
          masterData["CLOSING TIME"] || masterData.closingTime || null,
        hostelId: masterData._ID || masterData.id || null,
      });
    }
  }, [masterData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTimeChange = (timeField) => (time) => {
    setFormData((prev) => ({
      ...prev,
      [timeField]: time ? time.format("hh:mm A") : null,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Hostel Name is required";
    if (!formData.description?.trim())
      newErrors.description = "Description is required";
    if (!formData.openingTime)
      newErrors.openingTime = "Opening Time is required";
    if (!formData.closingTime)
      newErrors.closingTime = "Closing Time is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    handleConfirmedSubmit();
  };

  const handleConfirmedSubmit = async () => {
    setIsLoading(true);

    try {
      const response = masterData
        ? await updateHostel({
            ...formData,
            hostelId: formData.hostelId,
          })
        : await addHostel(formData);

      if (response.status === "success") {
        const myButton = document.getElementById("myToastButton");
        callToast(
          "success",
          "Success",
          `Hostel ${masterData ? "updated" : "added"} successfully!`,
          myButton
        );

        if (typeof refresh === "function") {
          await refresh({ columnRefresh: false, isLoading: false });
        }

        setTimeout(() => {
          closeModal();
          if (!masterData) {
            navigate("/organisation/hostel/hostel-list");
          }
        }, 2000);
      } else if (response.status === "fail") {
        showErrorMessages({ data: response, navigate });
        const myButton = document.getElementById("myToastButton");
        callToast(
          "error",
          "Error",
          response.message ||
            `Failed to ${masterData ? "update" : "add"} hostel`,
          myButton
        );
      }
    } catch (error) {
      showErrorMessages({
        data: {
          message: `Failed to ${masterData ? "update" : "add"} hostel`,
          errors: error.message,
        },
        navigate,
      });
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "An unexpected error occurred", myButton);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeValue = (timeString) => {
    return timeString ? moment(timeString, "hh:mm A") : null;
  };

  return (
    <>
      <Card.Body className="px-4 py-4">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Hostel Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter hostel name"
              isInvalid={!!errors.name}
              className="shadow-sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Opening Time</Form.Label>
                <div className="time-picker-wrapper">
                  <TimePicker
                    format="hh:mm A"
                    className={`custom-time-picker ${
                      errors.openingTime ? "is-invalid" : ""
                    }`}
                    style={{ width: "100%" }}
                    popupClassName="time-picker-dropdown"
                    placeholder="Select opening time"
                    value={getTimeValue(formData.openingTime)}
                    onChange={handleTimeChange("openingTime")}
                    use12Hours
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                  {errors.openingTime && (
                    <div className="invalid-feedback d-block">
                      {errors.openingTime}
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">Closing Time</Form.Label>
                <div className="time-picker-wrapper">
                  <TimePicker
                    format="hh:mm A"
                    className={`custom-time-picker ${
                      errors.closingTime ? "is-invalid" : ""
                    }`}
                    style={{ width: "100%" }}
                    popupClassName="time-picker-dropdown"
                    placeholder="Select closing time"
                    value={getTimeValue(formData.closingTime)}
                    onChange={handleTimeChange("closingTime")}
                    use12Hours
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                  {errors.closingTime && (
                    <div className="invalid-feedback d-block">
                      {errors.closingTime}
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter hostel description"
              rows={3}
              isInvalid={!!errors.description}
              className="shadow-sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="light" onClick={closeModal} className="px-3">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
              className="px-4"
            >
              {isLoading
                ? masterData
                  ? "Updating..."
                  : "Adding..."
                : masterData
                ? "Update"
                : "Submit"}
            </Button>
          </div>
        </Form>
      </Card.Body>
      <Modal
        show={showResponseModal}
        onHide={() => setShowResponseModal(false)}
        centered
        className={`response-modal ${modalConfig.type}`}
      >
        <Modal.Body className="text-center py-4">
          <div className={`response-icon ${modalConfig.type}`}>
            {modalConfig.type === "success" ? "✓" : "✕"}
          </div>
          <h5 className="mt-3">{modalConfig.title}</h5>
          <p className="mb-0">{modalConfig.message}</p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddHostelComponent;
